/*
 * 0.88: Mehrere Karten auf einmal – gemeinsamer Aufruf für das Einfügen ins Kartenfeld und den
 * Dialog „Karten importieren …“. Der Dialog wird über einen kleinen Zustand geöffnet, damit
 * Kartenfeld, Spaltenmenü und Board-Menü ihn ohne Durchreichen erreichen.
 */
import { useSyncExternalStore } from 'react';
import type { CardSummary, ImportPlan } from '@kaba/shared';
import { api, ApiError, newOpId } from './api';
import { boardKey, queryClient } from './board';
import { toast } from './toast';

export type CardImportSource = 'paste' | 'text' | 'csv' | 'xlsx' | 'planner' | 'asana' | 'jira' | 'todoist' | 'ticktick';

export interface CardImportRequest {
  boardId: string;
  plan: ImportPlan;
  source: CardImportSource;
  columnId: string;
  useSourceColumns?: boolean;
  placement?: 'top' | 'bottom';
  memberMap?: Record<string, string | null>;
}

export interface CardImportGateFailure {
  title: string;
  column: string;
  message: string;
}

/** Fehler „Spaltenregel nicht erfüllt“: Liste der betroffenen Karten (höchstens 20) und Gesamtzahl */
export function importGateFailures(err: unknown): { items: CardImportGateFailure[]; total: number } | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const d = (err.data as { details?: { importGates?: CardImportGateFailure[]; total?: number } } | undefined)?.details;
  return d?.importGates ? { items: d.importGates, total: d.total ?? d.importGates.length } : null;
}

const n = (x: number, one: string, many: string) => `${x.toLocaleString('de-DE')} ${x === 1 ? one : many}`;

/**
 * Importiert und meldet das Ergebnis mit „Rückgängig“. Fehler werden geworfen (der Dialog zeigt
 * Spaltenregel-Fehler selbst an). `onCreated` (Zeilen/Swimlanes): wird je neuer Karte aufgerufen.
 */
export async function runCardImport(req: CardImportRequest, onCreated?: (card: CardSummary) => void): Promise<{ cards: number; cardIds: string[] }> {
  const res = await api.post<{ cards: number; cardIds: string[]; columns: number; labels: number; droppedStarts: number }>(`/api/boards/${req.boardId}/import`, {
    plan: req.plan,
    source: req.source,
    columnId: req.columnId,
    useSourceColumns: !!req.useSourceColumns,
    placement: req.placement ?? 'bottom',
    memberMap: req.memberMap ?? {},
    opId: newOpId(),
  });
  void queryClient.invalidateQueries({ queryKey: boardKey(req.boardId) });
  if (onCreated) {
    for (const id of res.cardIds) {
      try {
        onCreated(await api.get<CardSummary>(`/api/cards/${id}`));
      } catch {
        /* Karte nicht mehr da (z. B. gleich archiviert) – nichts zu tun */
      }
    }
  }
  const extra = [res.columns ? n(res.columns, 'neue Spalte', 'neue Spalten') : '', res.labels ? n(res.labels, 'neues Label', 'neue Labels') : ''].filter(Boolean);
  const startNote = res.droppedStarts ? ` ${n(res.droppedStarts, 'Start lag', 'Starts lagen')} nach der Frist und ${res.droppedStarts === 1 ? 'wurde' : 'wurden'} weggelassen.` : '';
  toast.info(`${n(res.cards, 'Karte', 'Karten')} angelegt${extra.length ? ` (${extra.join(', ')})` : ''}.${startNote}`, {
    label: 'Rückgängig',
    run: () => {
      void api
        .post<{ archived: number }>(`/api/boards/${req.boardId}/import/undo`, { cardIds: res.cardIds })
        .then((r) => {
          void queryClient.invalidateQueries({ queryKey: boardKey(req.boardId) });
          toast.info(`${n(r.archived, 'Karte', 'Karten')} archiviert.${res.columns || res.labels ? ' Neu angelegte Spalten und Labels bleiben.' : ''}`);
        })
        .catch((err: Error) => toast.error(err.message));
    },
  });
  return res;
}

/* ---------- Dialog „Karten importieren …“ öffnen ---------- */

export interface CardImportDialogState {
  boardId: string;
  columnId?: string;
  /** vorbelegter Text (aus dem Kartenfeld) */
  text?: string;
}

let state: CardImportDialogState | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function openCardImport(s: CardImportDialogState) {
  state = s;
  emit();
}

export function closeCardImport() {
  state = null;
  emit();
}

export function useCardImportDialog(): CardImportDialogState | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}
